import type {
  ChangeEvent,
  Dispatch,
  JSX,
  PropsWithChildren,
  ReactElement,
  SetStateAction,
} from "react";
import { useCallback, useEffect, useState } from "react";
import type {
  Getters,
} from "@devexpress/dx-react-core";
import {
  Getter,
  Plugin,
} from "@devexpress/dx-react-core";
import type {
  Sorting,
  TableColumn as GridTableColumn,
} from "@devexpress/dx-react-grid";
import {
  IntegratedPaging,
  PagingState,
  RowDetailState,
} from "@devexpress/dx-react-grid";
import {
  Grid,
  PagingPanel,
  Table as TableGrid,
  TableColumnVisibility,
  TableHeaderRow,
  TableRowDetail,
} from "@devexpress/dx-react-grid-bootstrap4";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import * as E from "fp-ts/lib/Either";
import { flow, pipe } from "fp-ts/lib/function";
import * as O from "fp-ts/lib/Option";
import * as RA from "fp-ts/lib/ReadonlyArray";
import * as Th from "fp-ts/lib/These";
import { useStableO } from "fp-ts-react-stable-hooks";
import type { Store } from "redux";

import type { SortDocCategoriesC } from "@scripts/generated/models/document";
import type { SortItemsC } from "@scripts/generated/models/sortItems";
import { ExpandControlCell } from "@scripts/react/components/table/cells/ExpandControlCell";
import { WbrDataTypeProvider } from "@scripts/react/components/table/dataTypeProviders/WbrDataTypeProvider";
import type { ReactChild } from "@scripts/react/syntax/react";
import { klass, klassConditional } from "@scripts/react/util/classnames";
import type { GetRowStatus } from "@scripts/table/disableTableRows";

import { Empty, mapOrEmpty, trueOrEmpty } from "../Empty";
import type { SelectValueOnChange } from "../form/Select";
import { SelectRaw } from "../form/Select";
import { SearchInputRaw } from "../form/TextInput";
import { defaultPageSize } from "../paginator/Paginator";
import { NoDataCell, NoDataRow } from "./cells/NoDataCell";
import type { DataCellProps } from "./cells/TableDataCell";
import { DetailDataCell, TableDataCell, TableDataCellDraggable } from "./cells/TableDataCell";
import { draggableColumn, TableHeaderCell } from "./cells/TableHeaderCell";
import { TableBody, TableBodyDraggable } from "./TableBody";
import type { TableRowProps } from "./TableRow";
import { TableRow, TableRowDraggable, TableRowExpanded } from "./TableRow";
import { type ApiSort, columnValueFormat, type ExpandedRowIds, formatColumns, type HeaderCellProps, onDragApi, type OnDragEnd, onDragSortEnd, onExpandedRowIdsChange, type OnParamsChanged, onSearchChanged, pagingPanelOrEmpty, type SetData, type SortBy, type SortByVal, type SortColumnOption, sortLabelFormat, type SortOrderLens, type TableColumnRow, type TableColumnState, type TableRowModel, type TableSharedProps } from "./tableSyntax";
import { useTableData } from "./utils/useTableData";

const TableRootComponent = (p: PropsWithChildren<Grid.RootProps>) => <div {...klass("table-root")}>{p.children}</div>;

const TableContainer = (props: PropsWithChildren<{ isLgTable?: true, variant: TableVariant }>) => <div {...klass(klassConditional(props.isLgTable ? "table-lg-breakpoint" : "table-md-breakpoint")(props.variant === "portal"))}>{props.children}</div>;

type TableVariant = "portal" | "issuersites";

const TableContainerComponent = (tableError: boolean, variant: TableVariant, isHeaderless?: true) => (p: PropsWithChildren<unknown>) =>
  <div {...klass(
    variant === "portal" ? "portal-table-container" : "portal-table-sites",
    "w-100",
    isHeaderless ? "headerless" : O.none,
    tableError ? "table-error" : O.none,
  )}
  >
    {p.children}
  </div>;

function TableHeaderCellComponent(p: PropsWithChildren<TableHeaderRow.CellProps>) {
  return <TableHeaderCell
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    column={(p as HeaderCellProps<unknown, unknown, unknown>).column}
    row={p.tableRow.row}
  />;
}

type TableComponents<A, MetaData> = {
  bodyComponent: (p: React.PropsWithChildren<object>) => JSX.Element;
  containerComponent: (p: React.PropsWithChildren<object>) => JSX.Element;
  rowComponent: (expandedRowIds: ReadonlyArray<number>, columnsCount: number, getRowStatus?: GetRowStatus) =>
    (p: React.PropsWithChildren<TableRowProps<A, MetaData>>) => JSX.Element;
  cellComponent: (p: DataCellProps<A, MetaData>) => JSX.Element;
  detailCellComponent: (p: PropsWithChildren<TableRowDetail.CellProps>) => JSX.Element;
  columns: TableColumnState["columns"];
  columnExtensions: TableColumnState["columnExtensions"];
};

type TableActionsPanelProps<SB extends string> = {
  sortColumns: ReadonlyArray<SortColumnOption<SB>>;
  sortingSelected: ReadonlyArray<Sorting>;
  onSortChanged: (value: SelectValueOnChange<SB>) => void;
  onSearchChanged: (s: string) => void;
  tableAction: O.Option<TableAction>;
  searchable: O.Option<Searchable>;
};

export const TableActionSearch = <SB extends string>(props: Pick<TableActionsPanelProps<SB>, "onSearchChanged" | "searchable">) => {
  const [searchVal, setSearchVal] = useStableO<string>(O.map(([, v]: Searchable) => v)(props.searchable));
  const onSearchChangedCallback = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    pipe(e.target.value, O.fromPredicate((v) => v.length > 0), setSearchVal);
    props.onSearchChanged(e.target.value);
  }, [props, setSearchVal]);

  return mapOrEmpty(
    ([placeholder]: Searchable) => {
      const placeholderText = `Search ${pipe(placeholder, O.getOrElse(() => ""))}`;
      return <SearchInputRaw
        errorComponent={O.none}
        label={{ ariaLabel: "Search Input" }}
        onChange={onSearchChangedCallback}
        placeholder={O.some(placeholderText)}
        value={searchVal}
      />;
    })(props.searchable);
};

export const TableActionSort = <SB extends string>(props: Pick<TableActionsPanelProps<SB>, "sortColumns" | "onSortChanged" | "sortingSelected">) => {
  const sortOptions = props.sortColumns.map(sortLabelFormat);
  return trueOrEmpty(
    <div {...klass("sort")}>
      <SelectRaw
        value={pipe(RA.head(props.sortingSelected), O.chain(c => pipe(sortOptions, RA.findFirst(d => d.value === columnValueFormat(c)))))}
        labelOrAriaLabel={E.right("Sort Selection")}
        isSearchable={false}
        options={sortOptions}
        onChange={props.onSortChanged}
        error={O.none}
        placeholder={O.none}
        required={false}
      />
    </div>
  )(props.sortColumns.length > 0);
};

function TableActionsPanel<SB extends string>(props: TableActionsPanelProps<SB>) {
  const showPanel = O.isSome(props.searchable) || O.isSome(props.tableAction) || props.sortColumns.length > 0;

  return (
    showPanel
      ? <div {...klass("table-actions")}>
        <div {...klass("left-action-prepend")}>
          {pipe(props.tableAction,
            O.chain(Th.getLeft),
            mapOrEmpty((tableAction) => <div {...klass("table-action")}>{tableAction}</div>)
          )}
        </div>
        <div {...klass("left-table-actions", "order-2", "order-md-1")}>
          <TableActionSort
            sortingSelected={props.sortingSelected}
            onSortChanged={props.onSortChanged}
            sortColumns={props.sortColumns}
          />
        </div>
        <div {...klass("center-table-actions", "order-1", "order-md-2")}>
          {pipe(props.tableAction,
            O.chain(Th.getRight),
            mapOrEmpty((tableAction) => <div {...klass("table-action")}>{tableAction}</div>)
          )}
        </div>
        <div {...klass("right-table-actions", "order-3")}>
          <TableActionSearch
            searchable={props.searchable}
            onSearchChanged={props.onSearchChanged}
          />
        </div>
      </div>
      : <Empty />
  );
}

type TableRowExpandProps = {
  setExpandedRowIds: Dispatch<SetStateAction<number[]>>;
  expandedRowIds: number[];
};

type TableRowStatusProps = {
  getRowStatus?: GetRowStatus;
};

type TableBaseProps<
  A,
  MetaData,
  ExtraColumns extends string[],
  TCR extends TableColumnRow<A, MetaData, ExtraColumns> = TableColumnRow<A, MetaData, ExtraColumns>,
> = {
  data: ReadonlyArray<TCR["Row"]>;
  detailCell: O.Option<(row: TCR["Row"]) => ReactElement>;
  detailCellDisabled?: (row: TCR["Row"]) => boolean;
  collapseAllRows?: boolean;
  hiddenColumns?: ReadonlyArray<keyof TableColumnRow<A, MetaData, ExtraColumns>["Columns"]>;
} & TableRowStatusProps;

// eslint-disable-next-line @typescript-eslint/no-unsafe-return
const tableRowToModel = (p: TableRowDetail.ContentProps) => p.row;
const computedColumns = ({ tableColumns }: Getters) =>
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  Array.isArray(tableColumns)
    ? tableColumns.reduce((acc: GridTableColumn[], curr: GridTableColumn) => (curr.column && curr.column.name === draggableColumn.name) ? [curr, ...acc] : [...acc, curr], [])
    : tableColumns;


function TableBase<
  A,
  MetaData,
  ExtraColumns extends string[],
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters
  TCR extends TableColumnRow<A, MetaData, ExtraColumns> = TableColumnRow<A, MetaData, ExtraColumns>,
>(
  props: React.PropsWithChildren<TableBaseProps<A, MetaData, ExtraColumns>> & TableComponents<A, MetaData> & TableRowExpandProps & TableRowStatusProps
): ReactElement {


  /*
   ******************************  WARNING  ***********************************
   *
   * THIS MONKEY PATCHES THE CONSOLE TO PREVENT THE `defaultProps` ERROR - JDL
   *
   * This is to be removed when devexpress is updated from the Reactive to the
   * new React based library or the Reactive library is updated to remove the
   * usage of `defaultProps` which is unlikely.
   *
   * This prevents an upgrade to React 19.
   */

  /* eslint-disable no-console */
  const originalConsoleError = console.error;

  console.error = (...args: unknown[]) => {
    if (typeof args[0] === "string" && args[0].includes("defaultProps")) {
      return;
    }

    originalConsoleError(...args);
  };

  useEffect(() => {
    return () => {
      console.error = originalConsoleError;
    };
    /* eslint-enable no-console */
  }, [originalConsoleError]);
  /*
   ***************************  END WARNING  **********************************
   */


  const toggleCellComponent = useCallback(
    (p: PropsWithChildren<Omit<TableRowDetail.ToggleCellProps, "row"> & { row: TableRowModel<A, MetaData> }>) =>
      <ExpandControlCell {...p} disabled={props.detailCellDisabled?.(p.row)} />,
    [props]
  );

  const tableRowDetail = useCallback((dcc: (row: TCR["Row"]) => ReactElement) =>
    <TableRowDetail
      cellComponent={props.detailCellComponent}
      rowComponent={TableRowExpanded(props.getRowStatus)}
      contentComponent={flow(tableRowToModel, dcc)}
      toggleCellComponent={toggleCellComponent}
    />,
    [props.detailCellComponent, toggleCellComponent, props.getRowStatus]
  );

  useEffect(() => {
    props.collapseAllRows && props.setExpandedRowIds([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.collapseAllRows]);

  return (
    <Grid
      rootComponent={TableRootComponent}
      rows={props.data}
      columns={props.columns}
    >
      <WbrDataTypeProvider
        for={props.columnExtensions.reduce((acc: string[], curr) => curr.wbrEnabled ? [...acc, curr.columnName] : acc, [])}
      />
      {props.children}
      <TableGrid
        noDataRowComponent={NoDataRow}
        noDataCellComponent={NoDataCell(RA.isEmpty(props.data))}
        bodyComponent={props.bodyComponent}
        columnExtensions={props.columnExtensions}
        containerComponent={props.containerComponent}
        rowComponent={props.rowComponent(props.expandedRowIds, props.columns.length + (O.isSome(props.detailCell) ? 1 : 0), props.getRowStatus)}
        cellComponent={props.cellComponent}
      />
      <TableColumnVisibility hiddenColumnNames={props.hiddenColumns?.map(col => col.toString()) ?? []} />
      <TableHeaderRow cellComponent={TableHeaderCellComponent} />
      <RowDetailState
        expandedRowIds={props.expandedRowIds}
        onExpandedRowIdsChange={onExpandedRowIdsChange(props.setExpandedRowIds)}
        defaultExpandedRowIds={[]}
      />
      {mapOrEmpty(tableRowDetail)(props.detailCell)}
      {/*
        This Plugin is a Getter for values in the tables
        For getters to work they must exist within the Grid component.
      */}
      <Getter
        name="tableColumns"
        computed={computedColumns}
      />
    </Grid>
  );
}

export type TableAction = Th.These<ReactElement, ReactElement>;

export type TableProps<
  SB extends SortBy<keyof A>,
  A,
  MetaData,
  ExtraColumns extends string[],
  Params extends object,
> = TableBaseProps<A, MetaData, ExtraColumns>
  & TableSharedProps<A, MetaData, ExtraColumns>
  & {
    variant?: TableVariant;
    searchable: O.Option<Searchable>;
    paginate: O.Option<number>;
    tableAction: O.Option<TableAction>;
    onParamsChanged: OnParamsChanged<SB, Params>;
    sortable: O.Option<SB>;
    store: O.Option<Store>;
    params?: Partial<Params>;
    hideActionsPanel?: true;
    isLgTable?: true;
    pageSize?: number;
    customActionsPanel?: (props: TableCustomActionPanelProps<SB>) => ReactElement;
  } & TableRowStatusProps;

export type TableCustomActionPanelProps<SB extends string> = Omit<TableActionsPanelProps<SB>, "tableAction">;
export type Searchable = [placeholder: O.Option<string>, value: string, ignoreSearch?: true];

type TableRawProps<
  SB extends SortBy<keyof A>,
  A,
  MetaData,
  ExtraColumns extends string[],
  Params extends object,
  > = Omit<TableBaseProps<A, MetaData, ExtraColumns>, "data">
  & TableSharedProps<A, MetaData, ExtraColumns>
  & {
    columnSortList: ReadonlyArray<SortColumnOption<SB>>;
    expandedRowIds: ExpandedRowIds;
    variant?: TableVariant;
    searchable: O.Option<Searchable>;
    paginate: O.Option<number>;
    tableAction: O.Option<TableAction>;
    onParamsChanged: OnParamsChanged<SB, Params>;
    sortable: O.Option<SB>;
    store: O.Option<Store>;
    params?: Partial<Params>;
    hideActionsPanel?: true;
    isLgTable?: true;
    pageSize?: number;
    customActionsPanel?: (props: TableCustomActionPanelProps<SB>) => ReactElement;
    rows: ReadonlyArray<TableRowModel<A, MetaData>>;
    searchVal: string;
    setExpandedRowIds: Dispatch<SetStateAction<ExpandedRowIds>>;
    sortByVal: SortByVal<SB, A>;
    sorting: ReadonlyArray<Sorting>;
  } & TableRowStatusProps;

export const TableRaw = <
  SB extends SortBy<keyof A>,
  A,
  MetaData,
  ExtraColumns extends string[],
  Params extends object,
>(props: TableRawProps<SB, A, MetaData, ExtraColumns, Params>) => {
  const { onParamsChanged, params, searchVal } = props;

  const pagingPanel = useCallback((p: PropsWithChildren<PagingPanel.ContainerProps>) =>
    pagingPanelOrEmpty<SB, A, Params>(props.sortByVal, props.onParamsChanged, props.paginate, props.searchVal, p, props.params || {}),
    [props.onParamsChanged, props.paginate, props.params, props.searchVal, props.sortByVal]
  );

  const { columns, columnExtensions } = formatColumns(props.columns);

  const onSortChanged = useCallback((value: SelectValueOnChange<SB>) => {
    const partialParams: Partial<Params> = params ?? {};

    onParamsChanged({ ...partialParams, search: searchVal, sortBy: value.value, page: 1 });
  }, [onParamsChanged, params, searchVal]);

  return <TableContainer isLgTable={props.isLgTable} variant={props.variant ?? "portal"}>
    {pipe(
      O.fromNullable(props.customActionsPanel),
      O.fold(
        () => props.hideActionsPanel
          ? <Empty />
          : <TableActionsPanel
            onSearchChanged={onSearchChanged<SB, A, Params>(props.sortByVal, props.onParamsChanged, props.params || {})}
            onSortChanged={onSortChanged}
            searchable={pipe(props.searchable, O.map(([p, v]) => [p, v]))}
            tableAction={props.tableAction}
            sortColumns={props.columnSortList}
            sortingSelected={props.sorting}
          />,
        AP =>
          <AP
            onSearchChanged={onSearchChanged<SB, A, Params>(props.sortByVal, props.onParamsChanged, props.params || {})}
            onSortChanged={onSortChanged}
            searchable={pipe(props.searchable, O.map(([p, v]) => [p, v]))}
            sortColumns={props.columnSortList}
            sortingSelected={props.sorting}
          />
      )
    )}
    <TableBase
      columns={columns}
      detailCell={props.detailCell}
      cellComponent={TableDataCell(props.expandedRowIds)}
      detailCellComponent={DetailDataCell(false)}
      bodyComponent={TableBody}
      containerComponent={TableContainerComponent(false, props.variant ?? "portal", props.isHeaderless)}
      columnExtensions={columnExtensions}
      rowComponent={TableRow}
      data={props.rows}
      expandedRowIds={props.expandedRowIds}
      setExpandedRowIds={props.setExpandedRowIds}
      getRowStatus={props.getRowStatus}
      detailCellDisabled={props.detailCellDisabled}
      collapseAllRows={props.collapseAllRows}
      hiddenColumns={props.hiddenColumns}
    >
      <Plugin>
        {pipe(
          props.paginate,
          mapOrEmpty(pageNo => (
            <Plugin>
              <PagingState
                currentPage={pageNo - 1}
                defaultPageSize={props.pageSize ?? defaultPageSize}
              />
              <PagingPanel containerComponent={pagingPanel} />
              <IntegratedPaging />
            </Plugin>
          )
          )
        )}
      </Plugin>
    </TableBase>
  </TableContainer>;
};

export const Table = <
  SB extends SortBy<keyof A>,
  A,
  MetaData,
  ExtraColumns extends string[],
  Params extends object,
>(props: TableProps<SB, A, MetaData, ExtraColumns, Params>): ReactElement => {
  const tableData = useTableData(
    props.columns,
    props.data,
    props.searchable,
    props.sortable,
    props.store
  );

  return <TableRaw
    {...props}
    {...tableData}
  />;
};

type TableDraggableProps<A, MetaData, ExtraColumns extends string[]> = TableBaseProps<A, MetaData, ExtraColumns>
  & TableSharedProps<A, MetaData, ExtraColumns>
  & TableRowStatusProps & {
    isLgTable?: true;
    dragExplanation?: ReactChild;
  };

type TableDraggableRawProps<A, MetaData, ExtraColumns extends string[]> =
  TableDraggableProps<A, MetaData, ExtraColumns> & {
    expandedRowIds: ExpandedRowIds;
    setExpandedRowIds: React.Dispatch<SetStateAction<ExpandedRowIds>>;
    onDragEnd: (rows: OnDragEnd<A, MetaData>) => void;
  error: boolean;
};

const TableDraggableRaw = <A, MetaData, ExtraColumns extends string[]>(
  props: TableDraggableRawProps<A, MetaData, ExtraColumns>
): ReactElement => {
  const sensors = useSensors(useSensor(PointerSensor));
  const { columns, columnExtensions } = formatColumns(props.columns);

  return (
    <TableContainer isLgTable={props.isLgTable} variant="portal">
      {pipe(props.dragExplanation, O.fromNullable, mapOrEmpty(_ => <p {...klass("d-none-until-md", "mb-1")}>{_}</p>))}
      <p {...klass("d-lg-none", "mb-1")}>To reorder items in the table below, open this page on a device with a larger screen, such as a laptop or large tablet.</p>
      <DndContext
        sensors={sensors}
        modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        collisionDetection={closestCenter}
        onDragEnd={flow(onDragSortEnd(props.data, props.expandedRowIds), props.onDragEnd)}
      >
        <TableBase
          columns={[draggableColumn, ...columns]}
          detailCell={props.detailCell}
          cellComponent={TableDataCellDraggable(props.expandedRowIds)}
          detailCellComponent={DetailDataCell(true)}
          bodyComponent={TableBodyDraggable(pipe(props.data, RA.map(c => c.__rowId)))}
          containerComponent={TableContainerComponent(props.error, "portal", props.isHeaderless)}
          columnExtensions={columnExtensions}
          rowComponent={TableRowDraggable}
          setExpandedRowIds={props.setExpandedRowIds}
          expandedRowIds={props.expandedRowIds}
          data={props.data}
          getRowStatus={props.getRowStatus}
          hiddenColumns={props.hiddenColumns}
        />
      </DndContext>
    </TableContainer>
  );
};

export const TableDraggable = <
  A,
  MetaData,
  ExtraColumns extends string[],
  SortType extends SortItemsC | SortDocCategoriesC,
  >(props: TableDraggableProps<A, MetaData, ExtraColumns> & {
    draggable: ApiSort<A, MetaData, SortType>;
    setData: SetData<A, MetaData>;
    sortOrderlens: SortOrderLens<A, MetaData>;
  }): ReactElement => {
  const [expandedRowIds, setExpandedRowIds] = useState<ExpandedRowIds>([]);

  return (
    <TableDraggableRaw
      dragExplanation={props.dragExplanation}
      error={props.draggable.error}
      onDragEnd={onDragApi<A, MetaData, SortType>(props.data, props.draggable, props.setData, setExpandedRowIds, props.sortOrderlens)}
      data={props.data}
      detailCell={props.detailCell}
      columns={props.columns}
      getRowStatus={props.getRowStatus}
      expandedRowIds={expandedRowIds}
      setExpandedRowIds={setExpandedRowIds}
      collapseAllRows={props.collapseAllRows}
      detailCellDisabled={props.detailCellDisabled}
      isHeaderless={props.isHeaderless}
      hiddenColumns={props.hiddenColumns}
      isLgTable={props.isLgTable}
    />
  );
};

// When draggable api is not avalible for your use case or you are using a form use this table
export const TableDraggableControlled = <A, MetaData, ExtraColumns extends string[]>(
  props: TableDraggableProps<A, MetaData, ExtraColumns> & { setData: SetData<A, MetaData>, error: boolean }
) => {
  const [expandedRowIds, setExpandedRowIds] = useState<ExpandedRowIds>([]);

  return <TableDraggableRaw
    data={props.data}
    dragExplanation={props.dragExplanation}
    detailCell={props.detailCell}
    expandedRowIds={expandedRowIds}
    setExpandedRowIds={setExpandedRowIds}
    columns={props.columns}
    getRowStatus={props.getRowStatus}
    collapseAllRows={props.collapseAllRows}
    detailCellDisabled={props.detailCellDisabled}
    isHeaderless={props.isHeaderless}
    onDragEnd={O.map(([newRows, newExpandedRows]) => {
      props.setData(newRows);
      setExpandedRowIds(newExpandedRows);
    })}
    hiddenColumns={props.hiddenColumns}
    error={props.error}
    isLgTable={props.isLgTable}
  />;
};
